import ReactMarkdown from 'react-markdown';
import { useState } from 'react';
import { cn } from '@/utils/cn';
import type { Message } from '@/types';
import { AlertTriangle, Wrench, Check, Loader2, X, RefreshCw, Zap, HelpCircle, FileVideo } from 'lucide-react';
import { GddCard } from '@/components/gdd-card';
import { BuildCard } from '@/components/build-card';
import { VideoConfirmCard } from '@/components/video-confirm-card';
import { VideoMaterialConfirmCard } from '@/components/video-material-confirm-card';
import { VideoPlanConfirmCard } from '@/components/video-plan-confirm-card';
import { useAgentStore } from '@/store/agent-store';
import { ThinkingBlock } from '@/components/thinking-block';
import { SearchBlock } from '@/components/search-block';
import { ActionBlock } from '@/components/action-block';
import { ToolCallBlock } from '@/components/tool-call-block';
import { SessionActivity, type SessionActivityEvent } from '@/components/session-activity';
import { AttachmentPreview, type AttachmentPreviewSource } from '@/components/attachment-preview';

function resolveUploadedVideoUrl(video: { filePath?: string; url?: string }): string {
  if (video.url) return video.url;
  if (!video.filePath) return '';
  const fileName = video.filePath.replace(/\\/g, '/').split('/').pop();
  return fileName ? `/uploads/video/${encodeURIComponent(fileName)}` : '';
}

/**
 * BUG-20260815: GDD JSON 变长后 LLM 偶发漏掉最外层闭合括号（实测漏一个 `}`），
 * 前端 JSON.parse 失败 → 不出 GDD 卡片。此处按括号栈补全未闭合的 `}`/`]` 后重试，
 * 与后端 gdd.ts tryParseGddJson 的截断修复对齐。
 */
function tryCompleteJson(raw: string): string | null {
  const stack: string[] = [];
  let inStr = false;
  let esc = false;
  for (const ch of raw) {
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === '{') stack.push('}');
    else if (ch === '[') stack.push(']');
    else if (ch === '}' || ch === ']') stack.pop();
  }
  if (inStr || stack.length === 0) return null;
  return raw + stack.reverse().join('');
}

/**
 * LLM 流式输出有时只写出 `<GDD>{...`，在达到 token 上限前没有机会补上
 * `</GDD>`。这种消息仍然应渲染为 GDD 卡片，而不是把 JSON 当普通回答显示。
 */
function extractGddPayload(content: string): { raw: string; hasClosingTag: boolean } | null {
  const open = /<GDD\s*>/i.exec(content);
  if (!open) return null;
  const start = open.index + open[0].length;
  const tail = content.slice(start);
  const close = /<\/GDD\s*>/i.exec(tail);
  const raw = (close ? tail.slice(0, close.index) : tail)
    .replace(/<GDD_CONFIRM\s*\/?\s*>/gi, '')
    .trim();
  return { raw, hasClosingTag: !!close };
}

/** 微调三阶段定义（顺序即展示顺序，与后端 refine-graph 节点一一对应） */
const REFINE_STAGES = [
  { key: 'understand', node: 'refine_understand', label: '理解需求' },
  { key: 'apply', node: 'refine_apply', label: '应用修改' },
  { key: 'verify', node: 'refine_verify', label: '验证产物' },
] as const;

type RefineResult = { ok: boolean; baseVersion?: number; basePreviewUrl?: string; targetVersion?: number; newPreviewUrl?: string; error?: string; stage?: string; thoughts?: string[] };

// DO-NOT-REVERT: BUG-20260909-PW-005 打包发布内部日志行过滤——与后端 video-refine thought 上报过滤
// 同正则（P4 同步）：后端已保证新数据干净，此处兜底历史卡片 refineResult.thoughts 的存量日志行
// （刷新后仍可回看时不再污染意图优化卡，用户 2026-09-09 反馈）
const PACK_PUBLISH_LINE = /\[打包发布\]|正在构建试玩包|发布完成，可预览|预览地址[:：]/;

/**
 * 微调进度卡片：进行中实时读全局 refineState（SSE 驱动），结束后读消息级 refineResult（刷新历史仍可展示）。
 * 与生成流水线图完全隔离，不碰 pipelineState/nodeOutputs。
 */
function RefineCard({ result }: { result?: RefineResult }): JSX.Element {
  const refineState = useAgentStore((s) => s.refineState);
  const live = !result;
  const running = live && refineState.status === 'running';
  const failed = live ? refineState.status === 'failed' : !result.ok;
  const error = live ? refineState.error : result.error;
  // BUG-20260813-030: 思考轨迹双源——live 读全局 refineState 实时流，结束后读消息级 refineResult.thoughts（刷新可回看）
  const thoughts = (live ? refineState.thoughts : result?.thoughts)
    // P4: 打包发布内部日志不入卡片（视频链路 verify 阶段的真实 publish 日志，仅后端排查用）
    ?.filter((t) => !PACK_PUBLISH_LINE.test(t));
  const baseVersion = live ? refineState.baseVersion : result.baseVersion;
  const targetVersion = live ? refineState.targetVersion : result.targetVersion;

  const stageState = (key: string, node: string): 'done' | 'active' | 'failed' | 'pending' => {
    if (!live) {
      if (!failed) return 'done';
      // BUG-20260821-PW-001（评审加固）: 历史卡片按 refineResult.stage 标真实失败阶段（后端透传）——
      // 该阶段 failed、前置阶段 done、后置阶段 pending；stage 缺失（旧数据）保持原行为向后兼容
      const failStage = result.stage;
      if (!failStage) return key === 'verify' ? 'failed' : 'done';
      const failIdx = REFINE_STAGES.findIndex((s) => s.key === failStage);
      const curIdx = REFINE_STAGES.findIndex((s) => s.key === key);
      if (failIdx === -1 || curIdx === -1) return key === 'verify' ? 'failed' : 'done';
      return curIdx === failIdx ? 'failed' : curIdx < failIdx ? 'done' : 'pending';
    }
    const nodeStatus = refineState.nodeOutputs?.[node]?.status;
    if (nodeStatus === 'done') return 'done';
    if (nodeStatus === 'failed') return 'failed';
    if (failed) return 'pending';
    if (running && refineState.stage === key) return 'active';
    // 重试回路：verify 失败后回到 understand，此时后续阶段保持 pending
    return 'pending';
  };

  return (
    <div className="max-w-[80%] overflow-hidden rounded-xl border border-border/35 bg-[hsl(var(--card)/0.34)]">
      {/* 标题栏（与会话过程卡片同款样式） */}
      <div className="flex items-center gap-2 border-b border-border/25 px-3.5 py-2.5">
        <div className="flex h-5 w-5 items-center justify-center rounded-md bg-amber-500/10 text-amber-400">
          <Wrench size={12} />
        </div>
        {/* BUG-20260812-005: 回滚按钮按用户要求移除——旧版本仍在版本列表可切换预览，卡片不再提供回滚入口 */}
        <span className="flex-1 text-xs font-medium text-foreground/85">
          意图优化{baseVersion ? `（基于 V${baseVersion}）` : ''}{targetVersion ? ` → V${targetVersion}` : ''}
        </span>
        <span className="text-[10px] font-mono text-muted-foreground/70">
          {running ? '进行中' : failed ? '失败' : `${REFINE_STAGES.length} 阶段`}
        </span>
      </div>

      {/* 三阶段时间轴列表（与会话过程卡片列表同款样式） */}
      <div className="px-3.5 py-2">
        {REFINE_STAGES.map((s, index) => {
          const st = stageState(s.key, s.node);
          return (
            <div key={s.key} className="relative flex gap-2.5 py-2 first:pt-1 last:pb-1">
              {index < REFINE_STAGES.length - 1 && (
                <span aria-hidden className="absolute left-[6px] top-7 bottom-[-2px] w-px bg-border/45" />
              )}
              <span className={cn(
                'relative z-10 mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--card))]',
                st === 'active' && 'ring-2 ring-amber-500/20',
              )}>
                {st === 'done' && <Check size={14} className="text-neon-green" />}
                {st === 'failed' && <X size={14} className="text-red-400" />}
                {st === 'active' && <Loader2 size={14} className="animate-spin text-amber-400" />}
                {st === 'pending' && <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <p className={cn(
                    'min-w-0 flex-1 text-xs leading-5',
                    st === 'active' ? 'text-foreground' : 'text-foreground/75',
                  )}>{s.label}</p>
                  {st === 'done' && <span className="shrink-0 text-[10px] text-neon-green/70">完成</span>}
                  {st === 'failed' && <span className="shrink-0 text-[10px] text-red-400/80">失败</span>}
                  {st === 'active' && <span className="shrink-0 text-[10px] text-amber-400/80">进行中</span>}
                </div>
                {st === 'failed' && error && (
                  <p className="mt-0.5 break-all text-[10px] leading-4 text-red-400/80">{error}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* BUG-20260813-029/030: 思考过程实时流——running 时滚动展示最近思考避免干等；结束/失败后保留轨迹可回看 */}
      {thoughts && thoughts.length > 0 && (
        <div className="border-t border-border/20 px-3.5 py-2">
          <div className="flex flex-col gap-1">
            {thoughts.slice(-3).map((t, i, arr) => (
              <p key={`${thoughts.length}-${i}`} className={cn(
                'flex items-start gap-1.5 text-[11px] leading-relaxed',
                i === arr.length - 1 ? 'text-amber-300/90' : 'text-muted-foreground/80',
              )}>
                {i === arr.length - 1 && running
                  ? <Loader2 size={11} className="mt-0.5 shrink-0 animate-spin text-amber-400" />
                  : <span className="mt-0.5 shrink-0 text-muted-foreground/50">·</span>}
                <span className="break-all">{t}</span>
              </p>
            ))}
          </div>
        </div>
      )}
      {!live && !result.ok && (
        <div className="border-t border-border/20 px-3.5 py-2">
          <p className="text-[11px] text-muted-foreground">当前预览保持意图优化前版本不变，可换个说法重试。</p>
        </div>
      )}
    </div>
  );
}

interface ChatMessageProps {
  message: Message;
  /** 是否为会话中最新一条 GDD 卡片（FR-11：旧 GDD 卡片置灰，仅最新可交互） */
  isLatestGdd?: boolean;
  onConfirmGdd?: (msgId: string) => void;
  onChoice?: (field: string, value: string, msgId?: string) => void;
  onRetry?: (messageId: string) => void;
  /** 重复指令二次确认：用户选择复用/重新生成 */
  onDuplicateChoice?: (choice: 'reuse' | 'regenerate') => void;
  /** 视频转试玩确认：用户点击确认生成 */
  onVideoConfirm?: (messageId: string) => void;
  /** 视频素材确认卡片：用户确认视频顺序与图片槽位 */
  onVideoMaterialConfirm?: (messageId: string, videoOrder: string[], imageRoles: Array<{ fileName: string; role: string }>, videoOrderIndexes?: number[]) => void;
  /** 视频方案确认卡片（FEAT-20260907-VIDEO-PLAN）：用户确认方案或倒计时自动触发 */
  onVideoPlanConfirm?: (messageId: string) => void;
  /** BUG-20260908-PW-001: 会话管线状态——running/done 时禁方案卡倒计时自动重发 */
  videoPipelineStatus?: 'running' | 'done' | 'paused' | null;
}

export function ChatMessage({
  message,
  isLatestGdd = true,
  onConfirmGdd,
  onChoice,
  onRetry,
  onDuplicateChoice,
  onVideoConfirm,
  onVideoMaterialConfirm,
  onVideoPlanConfirm,
  videoPipelineStatus,
}: ChatMessageProps): JSX.Element {
  const isUser = message.role === 'user';
  const hasError = message.metadata?.error;
  const content = message.content ?? '';
  const isStreaming = message.metadata?.streaming;
  const [attachmentPreview, setAttachmentPreview] = useState<AttachmentPreviewSource | null>(null);

  // Parse GDD and CHOICE blocks
  const gddPayload = extractGddPayload(content);
  const eventGddSpec = message.metadata?.gddSpec;
  const hasStableGddSpec = !!eventGddSpec && typeof eventGddSpec === 'object' && !Array.isArray(eventGddSpec);
  let gddSpec: Record<string, unknown> | null = null;
  let gddParseFailed = false;
  // 流式 GDD 原文会在每个 token 到达时处于“可解析/半截 JSON”两种状态之间，
  // 不能在这期间据此切换消息布局。生成中只使用服务端独立 gdd 事件的稳定 spec；
  // 流结束后再从历史文本解析，兼容旧消息。
  if (gddPayload && (!isStreaming || hasStableGddSpec)) {
    const raw = gddPayload.raw
      .replace(/:\s*,/g, ': "",')       // "key": , → "key": "",
      .replace(/:\s*}/g, ': ""}')       // "key": } → "key": ""}
      .replace(/,\s*,/g, ',')           // ,, → ,
      .replace(/,\s*}/g, '}')           // trailing comma in objects
      .replace(/,\s*]/g, ']');          // trailing comma in arrays
    try {
      gddSpec = JSON.parse(raw);
    } catch {
      // BUG-20260815: 漏闭合括号时补全后重试（LLM 偶发漏最外层 `}`）
      const completed = tryCompleteJson(raw);
      if (completed) {
        try { gddSpec = JSON.parse(completed); } catch { gddParseFailed = true; }
      } else {
        gddParseFailed = true;
      }
    }
  }
  // 优先使用服务端 gdd SSE 事件附带的已校验 spec。流式文本可能暂时只有半截 JSON，
  // 或者后端已从可修复文本中解析出 GDD 但原文没有完整闭合标签；不能让卡片跟着 token 来回挂载。
  if (!gddSpec && hasStableGddSpec) {
    gddSpec = eventGddSpec as Record<string, unknown>;
    gddParseFailed = false;
  }

  const choiceMatch = content.match(/<CHOICE>([\s\S]*?)<\/CHOICE>/i);
  let choiceData: { field: string; title: string; options?: Array<{ value: string; label: string; desc?: string }> } | null = null;
  if (choiceMatch) try { choiceData = JSON.parse(choiceMatch[1].trim().replace(/,\s*}/g, '}').replace(/,\s*]/g, ']')); } catch { /* */ }

  // If GDD parse failed, keep the block in text so user can see it
  const cleanText = (gddParseFailed
    ? content.replace(/<GDD>[\s\S]*$/i, '').replace(/<CHOICE>[\s\S]*?<\/CHOICE>/gi, '').replace(/\n{3,}/g, '\n\n').trim()
    : content.replace(/<GDD>[\s\S]*?<\/GDD>/gi, '').replace(/<GDD>[\s\S]*$/i, '').replace(/<GDD_CONFIRM\s*\/?>/gi, '').replace(/<CHOICE>[\s\S]*?<\/CHOICE>/gi, '').replace(/\n{3,}/g, '\n\n').trim()
  ).replace(/<[A-Z_]*$/i, '').trimEnd(); // 打字机恢复：剔除流式消息尾部半截标签（如 <G / <CHOI），防残片闪现
  const isGddMessage = /<GDD/i.test(content) || !!gddSpec || !!message.metadata?.gddEvent;
  // 打字机恢复（用户需求）：正常问答流式逐字渲染 cleanText；仅 GDD/CHOICE 卡片块流式进行中继续
  // 显示思考态——半截 JSON 会刷屏（卡片场景不输出大段文本的原有保护保留）。
  const hasCardBlock = isGddMessage || /<CHOICE/i.test(content);
  const isRefinement = !!message.metadata?.isRefinement;
  const hasBuildResult = message.metadata?.eventType === 'playable:ready' || message.metadata?.playableReady;
  const duplicateConfirm = message.metadata?.duplicateConfirm as Record<string, unknown> | undefined;
  const videoConfirmation = message.metadata?.videoConfirmation as any;
  const videoMaterialConfirm = message.metadata?.videoMaterialConfirm as any;
  const videoPlanConfirm = message.metadata?.videoPlanConfirm as any;
  const hasCards = gddSpec || (choiceData && choiceData.options) || isRefinement || hasBuildResult || duplicateConfirm || videoConfirmation || videoMaterialConfirm || videoPlanConfirm;

  const textBubble = (
    <div className={cn('rounded-xl px-4 py-3 text-sm leading-relaxed',
      // 提亮：用户气泡边框/背景各提一档；AI 气泡背景提亮一档+明确边框，与页面背景形成可辨识边界
      isUser ? 'bg-primary/15 border border-primary/30 text-foreground' : 'glass-card bg-card/80 border-border/70',
      hasError && 'border-red-500/30 bg-red-500/5')}>
      {message.metadata?.toolName && (<div className="mb-2 flex items-center gap-1.5 text-xs text-neon-cyan"><Wrench size={12} />{message.metadata.toolName}</div>)}
      {/* 用户发送的图片附件缩略图（发送后即展示，配合自然语言描述替换/参考用途） */}
      {isUser && (() => {
        const imgs = (message.metadata?.uploadedImages ?? []) as Array<{ assetId?: string; url: string; fileName?: string }>;
        if (!imgs.length) return null;
        return (
          <div className="mb-2 grid grid-cols-3 gap-1.5">
            {imgs.map((img) => (
              <button
                key={img.assetId ?? img.url}
                type="button"
                onClick={() => img.url && setAttachmentPreview({ kind: 'image', url: img.url, name: img.fileName ?? '上传图片' })}
                className="group/img relative aspect-square overflow-hidden rounded-lg bg-muted text-left focus:outline-none focus:ring-2 focus:ring-primary/60"
                title="预览图片"
              >
                {img.url ? (
                  <img src={img.url} alt={img.fileName ?? '上传图片'} className="h-full w-full object-cover transition-transform group-hover/img:scale-105" />
                ) : (
                  <div className="flex h-full items-center justify-center text-[10px] text-muted-foreground">{img.fileName ?? '图片'}</div>
                )}
                {img.fileName && (
                  <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1 py-px text-[9px] text-white/90">{img.fileName}</span>
                )}
              </button>
            ))}
          </div>
        );
      })()}
      {/* 用户发送的视频附件：点击文件条目使用原生播放器预览。 */}
      {isUser && (() => {
        const vids = (message.metadata?.uploadedVideos ?? []) as Array<{ filePath?: string; url?: string; fileName?: string; originalName?: string }>;
        if (!vids.length) return null;
        return (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {vids.map((v, i) => {
              const previewUrl = resolveUploadedVideoUrl(v);
              const name = v.fileName ?? v.originalName ?? '视频';
              return (
              <button
                key={v.filePath ?? i}
                type="button"
                onClick={() => previewUrl && setAttachmentPreview({ kind: 'video', url: previewUrl, name })}
                disabled={!previewUrl}
                className="flex w-[132px] items-center gap-1.5 overflow-hidden rounded-lg border border-border/60 bg-muted/60 px-2 py-1.5 text-left transition-colors hover:border-primary/40 hover:bg-muted disabled:cursor-default"
                title={previewUrl ? '预览视频' : name}
              >
                <FileVideo size={14} className="shrink-0 text-neon-cyan" />
                <span className="truncate text-[10px] text-foreground/80">
                  {name}
                </span>
              </button>
              );
            })}
          </div>
        );
      })()}
      <AttachmentPreview source={attachmentPreview} onClose={() => setAttachmentPreview(null)} />
      {isUser
        ? <p className="whitespace-pre-wrap break-words leading-relaxed">{cleanText}</p>
        : isStreaming && (!cleanText || hasCardBlock)
        ? <div className="flex items-center gap-2 py-1 text-muted-foreground">
            <Loader2 size={14} className="animate-spin text-neon-cyan" />
            <span className="text-xs">AI 正在思考...</span>
          </div>
        : <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-neon-cyan prose-code:before:content-none prose-code:after:content-none prose-headings:text-foreground prose-strong:text-foreground prose-a:text-neon-cyan"><ReactMarkdown>{cleanText}</ReactMarkdown></div>}
      {hasError && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
          <AlertTriangle size={12} />
          {message.metadata.error}
          {onRetry && (
            <button
              onClick={() => onRetry(message.id)}
              className="ml-1 flex items-center gap-1 rounded border border-red-500/30 px-1.5 py-0.5 text-[11px] text-red-400 transition-colors hover:bg-red-500/10 hover:border-red-500/50"
            >
              <RefreshCw size={11} />
              重试
            </button>
          )}
        </div>
      )}
    </div>
  );

  // GDD 卡片已承载核心信息 — 仅当 GDD 解析成功时才隐藏原始文本，避免大段文本刷屏
  const showText = cleanText && !gddSpec;

  // 检测是否有工具调用或需要结构化展示
  const hasToolCall = !isUser && message.metadata?.toolName;
  const thinkingData = message.metadata?.thinking as { content: string; duration?: number } | undefined;
  const searchData = message.metadata?.search as { query?: string; count: number; results?: any[] } | undefined;
  const actionData = message.metadata?.action as { title: string; items: any[] } | undefined;
  const activityData = (message.metadata?.activity as SessionActivityEvent[] | undefined) ?? [];
  const hasActivity = activityData.length > 0;
  const hasStructuredData = thinkingData || searchData || actionData || hasToolCall || hasActivity;
  const showStructured = !isUser && !hasCards && (hasStructuredData || showText); // 有结构化数据或文本就显示

  // 用户消息带图片附件时（可能无文本），必须走简单气泡布局渲染缩略图——
  // 空文本会落入 cards 布局导致图片不可见（需求：发送的图片要在聊天记录展示）
  const hasUploadedImages = isUser && ((message.metadata?.uploadedImages as unknown[]) ?? []).length > 0;
  const hasUploadedVideos = isUser && ((message.metadata?.uploadedVideos as unknown[]) ?? []).length > 0;

  // Simple layout for user messages only
  if ((isUser && showText) || hasUploadedImages || hasUploadedVideos) {
    // 本会话区不展示任何头像（用户要求：AI 侧与用户侧头像均移除）
    return (
      <div id={message.id} className={cn('flex animate-slide-up', 'flex-row-reverse')}>
        <div className="max-w-[80%]">{textBubble}</div>
      </div>
    );
  }

  // Structured AI message layout (with tool calls or long content)
  if (showStructured) {
    // AI 侧头像已移除（用户要求）；卡片靠左贴边，最宽 80%（用户指定）
    return (
      <div id={message.id} className="animate-slide-up">
        <div className="min-w-0 max-w-[80%] space-y-2">
          <SessionActivity events={activityData} streaming={!!isStreaming} />
          {/* 思考过程：streaming 中显示加载态（spinner+自计秒数），完成后显示已思考 */}
          {thinkingData && !hasActivity && (
            <ThinkingBlock
              content={thinkingData.content}
              duration={thinkingData.duration}
              defaultExpanded={false}
              thinking={!!isStreaming}
              thinkingLabel={isGddMessage ? '正在生成游戏设计文档' : undefined}
            />
          )}

          {/* 搜索结果 */}
          {searchData && !hasActivity && (
            <SearchBlock
              query={searchData.query}
              count={searchData.count}
              results={searchData.results}
              defaultExpanded={false}
            />
          )}

          {/* 操作详情 */}
          {actionData && !hasActivity && (
            <ActionBlock
              title={actionData.title}
              items={actionData.items}
              defaultExpanded={false}
            />
          )}

          {/* 工具调用卡片 */}
          {hasToolCall && !hasActivity && (
            <ToolCallBlock
              toolName={message.metadata?.toolName as string}
              defaultExpanded={false}
            />
          )}

          {/* AI 回答内容 - 使用卡片式布局 */}
          {showText && cleanText && (
            <div className="px-1 py-1">
              {isStreaming && (!cleanText || hasCardBlock) ? (
                <div className="flex items-center gap-2 py-1 text-muted-foreground">
                  <Loader2 size={14} className="animate-spin text-neon-cyan" />
                  <span className="text-xs">{isGddMessage ? '正在生成游戏文档' : 'AI 正在思考...'}</span>
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-code:text-neon-cyan prose-code:before:content-none prose-code:after:content-none prose-headings:text-foreground prose-strong:text-foreground prose-a:text-neon-cyan">
                  <ReactMarkdown>{cleanText}</ReactMarkdown>
                </div>
              )}
              {hasError && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-red-400">
                  <AlertTriangle size={12} />
                  {message.metadata.error}
                  {onRetry && (
                    <button
                      onClick={() => onRetry(message.id)}
                      className="ml-1 flex items-center gap-1 rounded border border-red-500/30 px-1.5 py-0.5 text-[11px] text-red-400 transition-colors hover:bg-red-500/10 hover:border-red-500/50"
                    >
                      <RefreshCw size={11} />
                      重试
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Cards layout
  // FR-10/11：锁定只看消息级 metadata.gddConfirmed（确认时持久化到后端），不受全局 gddConfirmed 重置影响——
  // 确认后永锁；新 GDD 产生新消息新卡片，旧卡片一律整体置灰且不再展示确认按钮（无论是否确认过，防止误点旧卡片）。
  const gddLocked = !!message.metadata?.gddConfirmed;
  const gddStale = !isLatestGdd;
  const gddDimmed = gddStale;
  // AI 侧头像已移除（用户要求）；卡片靠左贴边，最宽 80%（用户指定）
  return (
    <div id={message.id} className="animate-slide-up">
      <div className="min-w-0 max-w-[80%] space-y-3">
        <SessionActivity events={activityData} streaming={!!isStreaming} />
        {gddSpec && (<GddCard spec={gddSpec as Record<string, string | number | string[]>} confirmed={gddLocked} locked={gddLocked} dimmed={gddDimmed} onConfirm={(gddLocked || gddStale) ? undefined : () => onConfirmGdd?.(message.id)} />)}
        {gddParseFailed && (<div className="glass-card rounded-xl p-3 text-xs text-amber-400">⚠️ GDD 解析失败</div>)}

        {isRefinement && <RefineCard result={message.metadata?.refineResult as RefineResult | undefined} />}

        {hasBuildResult && (<BuildCard previewUrl={(message.metadata as Record<string,unknown>)?.previewUrl as string} />)}

        {duplicateConfirm && (
          <div className="max-w-full space-y-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-5 py-4">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/20">
                <AlertTriangle size={14} className="text-amber-400" />
              </div>
              <p className="text-sm font-semibold text-amber-400">检测到重复指令</p>
            </div>
            <p className="text-xs text-muted-foreground">该指令与上次生成完全相同。你想要：</p>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => onDuplicateChoice?.('reuse')}
                className="group rounded-xl border border-border/50 bg-card/40 p-3.5 text-left transition-all hover:border-neon-cyan/40 hover:bg-neon-cyan/5 hover:shadow-lg hover:shadow-neon-cyan/10">
                <div className="mb-2 flex items-center gap-2">
                  <Zap size={14} className="text-neon-cyan" />
                  <p className="text-xs font-semibold text-foreground">复用上次试玩</p>
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground">直接复用上次生成的产物，立即预览（秒回）</p>
              </button>
              <button onClick={() => onDuplicateChoice?.('regenerate')}
                className="group rounded-xl border border-border/50 bg-card/40 p-3.5 text-left transition-all hover:border-neon-purple/40 hover:bg-neon-purple/5 hover:shadow-lg hover:shadow-neon-purple/10">
                <div className="mb-2 flex items-center gap-2">
                  <RefreshCw size={14} className="text-neon-purple" />
                  <p className="text-xs font-semibold text-foreground">生成不一样的效果</p>
                </div>
                <p className="text-[11px] leading-relaxed text-muted-foreground">绕过缓存重新生成，引入受控变化</p>
              </button>
            </div>
          </div>
        )}

        {videoConfirmation && (
          <VideoConfirmCard
            confirmation={videoConfirmation}
            confirmed={Boolean(message.metadata?.videoConfirmed)}
            onConfirm={() => onVideoConfirm?.(message.id)}
          />
        )}

        {videoMaterialConfirm && (
          <VideoMaterialConfirmCard
            material={videoMaterialConfirm}
            confirmed={Boolean(message.metadata?.materialConfirmed)}
            onConfirm={(videoOrder, imageRoles, videoOrderIndexes) => onVideoMaterialConfirm?.(message.id, videoOrder, imageRoles, videoOrderIndexes)}
          />
        )}

        {videoPlanConfirm && videoPlanConfirm.plan && (
          <VideoPlanConfirmCard
            plan={videoPlanConfirm.plan}
            autoStartSec={videoPlanConfirm.autoStartSec ?? 10}
            confirmed={Boolean(message.metadata?.planConfirmed)}
            suppressAutoStart={videoPipelineStatus === 'running' || videoPipelineStatus === 'done'}
            onConfirm={() => onVideoPlanConfirm?.(message.id)}
          />
        )}

        {choiceData?.options && (() => {
          // FR-12：选中态只看消息级 metadata.choiceValue（选择时持久化到后端）——
          // 不再用全局 choices 锁定，避免新一轮同 field 卡片被历史选择误锁。
          const choiceValue = message.metadata?.choiceValue as string | undefined;
          const choiceLocked = !!choiceValue;
          return (
          <div className="max-w-full space-y-3 rounded-2xl border border-neon-purple/20 bg-[hsl(var(--accent)/0.04)] px-5 py-4">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neon-purple/20">
                <HelpCircle size={14} className="text-neon-purple" />
              </div>
              <p className="text-sm font-semibold text-neon-purple">{choiceData.title}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {choiceData.options.map(opt => {
                const isSelected = choiceValue === opt.value;
                return (
                <button key={opt.value}
                  onClick={() => !choiceLocked && onChoice?.(choiceData!.field, opt.value, message.id)}
                  disabled={choiceLocked && !isSelected}
                  className={cn(
                    'group rounded-xl border p-3.5 text-left transition-all',
                    isSelected
                      ? 'border-neon-cyan/50 bg-neon-cyan/10 shadow-lg shadow-neon-cyan/20'
                      : choiceLocked
                        ? 'border-border/20 bg-muted/10 opacity-40 cursor-not-allowed'
                        : 'border-border/50 bg-card/40 hover:border-neon-purple/40 hover:bg-neon-purple/5 hover:shadow-lg hover:shadow-neon-purple/10',
                  )}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-foreground">{opt.label}</p>
                    {isSelected && <Check size={14} className="text-neon-cyan" />}
                  </div>
                  {opt.desc && <p className="text-[11px] leading-relaxed text-muted-foreground">{opt.desc}</p>}
                </button>
                );
              })}
            </div>
          </div>
          );
        })()}

        {showText && textBubble}
      </div>
    </div>
  );
}
